import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { gettext } from '@eventbrite/i18n';
import { setWindowLocation } from '@eb/http';
import { Icon } from '@eb/eds-icon';

import { BucketHeader } from '../../../../components/BucketHeader';
import { SeeMoreButton } from '../../../../components/SeeMoreButton';
import {
    CATEGORY_NAME_ICON_MAP,
    CATEGORY_TEMPLATE,
} from '../../../../constants';
import {
    GA_CITY_BROWSE_CATEGORY,
    GA_VIEW_CATEGORY_ACTION,
    GA_VIEW_MORE_ACTION,
} from '../../../../constants/analytics';

import './categoryBucket.scss';

const Category = ({ name, url, isCarousel, iconType, trackClick }) => {
    const classes = classNames('eds-link--neutral', 'category-item', {
        'category-item--mobile': isCarousel,
        'eds-g-cell': !isCarousel,
        'eds-g-cell-2-12': !isCarousel,
    });

    return (
        <a href={url} className={classes} onClick={trackClick.bind(null, name)}>
            <div className="eds-align--center">
                <Icon type={iconType} size="medium" hasHalo={true} />
            </div>
            <div className="eds-text--center eds-l-pad-top-4">{name}</div>
        </a>
    );
};

const _getRenderedCategories = (categories, isCarousel, trackClick) =>
    categories.map(({ url, name, id }) => {
        const iconType = CATEGORY_NAME_ICON_MAP[id];

        if (!iconType) {
            // logger.error(`Category icon is undefined in CityBrowse CategoryBucket.js for type: ${id}`);

            return null;
        }

        return (
            <Category
                name={name}
                url={url}
                key={id}
                iconType={iconType}
                isCarousel={isCarousel}
                trackClick={trackClick}
            />
        );
    });

const CategoryGroup = ({
    categories,
    trackClick,
    trackSeeMoreClick,
    seeMoreText,
    seeMoreUrl,
    title,
}) => {
    const isCarousel = categories.length > 6;

    const classes = classNames('eds-show-up-md', {
        'mobile-card-carousel': isCarousel,
        'eds-g-group': !isCarousel,
    });

    return (
        <div className="eds-l-mar-top-6 eds-l-mn-mar-top-10 eds-l-md-mar-top-10 eds-l-mw-mar-top-10 eds-l-ln-mar-top-10 eds-l-lg-mar-top-10 eds-l-lw-mar-top-10">
            <div className={classes}>
                {_getRenderedCategories(categories, isCarousel, trackClick)}
            </div>
            <div className="mobile-card-carousel eds-show-down-mn">
                {_getRenderedCategories(categories, true, trackClick)}
            </div>
            <div className="eds-l-pad-top-6">
                <div className="eds-show-down-sn eds-align--center">
                    <SeeMoreButton
                        title={title}
                        linkUrl={seeMoreUrl}
                        trackClick={trackSeeMoreClick}
                        seeMoreText={seeMoreText}
                        gaAction={GA_VIEW_MORE_ACTION}
                    />
                </div>
            </div>
        </div>
    );
};

export class CategoryBucket extends PureComponent {
    static propTypes = {
        /*
         * Array of preformatted objects that will represent that entities within the bucket
         */
        content: PropTypes.arrayOf(
            PropTypes.shape({
                id: PropTypes.string.isRequired,
                name: PropTypes.string.isRequired,
                url: PropTypes.string.isRequired,
            }),
        ).isRequired,
        /*
         * The name of the given bucket ie. "Upcoming Music Events"
         */
        name: PropTypes.string.isRequired,
        /*
         * Url for all events directory page
         */
        allEventsUrl: PropTypes.string,
        /*
         * Track GA Events from state
         */
        trackGAEvent: PropTypes.func,
        /*
         * Track GA Events from state on PageUnload
         */
        trackGAEventOnPageUnload: PropTypes.func,
    };

    _handleTrackClick(name, trackGAEvent) {
        trackGAEvent({
            category: GA_CITY_BROWSE_CATEGORY,
            action: GA_VIEW_CATEGORY_ACTION,
            label: name,
        });
    }

    _trackSeeMoreClick = (action, url) => {
        const { name, trackGAEventOnPageUnload } = this.props;

        if (name) {
            trackGAEventOnPageUnload({
                category: GA_CITY_BROWSE_CATEGORY,
                action,
                label: name,
            });
        }

        /* Need to fire tracking action on the seemore, so necessary to
        manually set windowlocation rather that relying on the link. */
        if (url) {
            setWindowLocation(url);
        }
    };

    render() {
        const { content, name, allEventsUrl, trackGAEvent } = this.props;
        const seeMoreText = gettext('Search all events').toString();

        return (
            <div>
                <BucketHeader
                    title={name}
                    linkUrl={allEventsUrl}
                    seeMoreText={seeMoreText}
                    template={CATEGORY_TEMPLATE}
                    trackGAEvent={trackGAEvent}
                />
                <CategoryGroup
                    categories={content}
                    trackClick={this._handleTrackClick.bind(this)}
                    trackSeeMoreClick={this._trackSeeMoreClick.bind(this)}
                    title={name}
                    seeMoreUrl={allEventsUrl}
                    seeMoreText={seeMoreText}
                />
            </div>
        );
    }
}
